import './MenuItemCard.css';

import FoodImage from './FoodImage';
import NutriScoreDisplay from './NutriScoreDisplay';
import { getRandomCalories } from '../utils/utils';
import { useState } from 'react';

const MenuItemCard = ({ item, index, onItemClick, onUpgradeClick, userPlan = 'free' }) => {
  const [showUpgradeTooltip, setShowUpgradeTooltip] = useState(false);

  const menuName = typeof item === 'string' ? item : item.menuResponse;
  const imageUrls = typeof item === 'object' ? item.imageUrls : null;
  const nutritionalScore = typeof item === 'object' ? item.nutritional_score : null;
  const approximateCalories = typeof item === 'object' ? item.approximate_calories : null;
  const price = typeof item === 'object' ? item.price : null;
  const isDemoLocked = typeof item === 'object' ? item.isDemoLocked : false;

  // Function to get high-risk allergens for paying users
  const getHighRiskAllergens = () => {
    if (typeof item !== 'object' || userPlan === 'free') return [];

    const allergenMap = {
      milk: { name: 'Milk', icon: '🥛' },
      cereals_containing_gluten: { name: 'Gluten', icon: '🌾' },
      eggs: { name: 'Eggs', icon: '🥚' },
      peanuts: { name: 'Peanuts', icon: '🥜' },
      nuts: { name: 'Tree Nuts', icon: '🌰' },
      soybeans: { name: 'Soybeans', icon: '🌱' },
      fish: { name: 'Fish', icon: '🐟' },
      crustaceans: { name: 'Crustaceans', icon: '🦐' },
      molluscs: { name: 'Molluscs', icon: '🦪' },
      sesame_seeds: { name: 'Sesame Seeds', icon: '⚫' },
      celery: { name: 'Celery', icon: '🥬' },
      mustard: { name: 'Mustard', icon: '🌿' },
      sulphur_dioxide_and_sulphites: { name: 'Sulphites', icon: '🧪' },
      lupin: { name: 'Lupin', icon: '🫘' },
    };

    const highRiskAllergens = [];

    Object.entries(allergenMap).forEach(([key, { name, icon }]) => {
      if (item[key] === 'high') {
        highRiskAllergens.push({ name, icon });
      }
    });

    return highRiskAllergens;
  };

  // Function to render dietary emojis
  const renderDietaryEmojis = () => {
    if (typeof item !== 'object') return null;

    const emojis = [];

    if (item.is_vegetarian === true) {
      emojis.push(
        <span key="vegetarian" className="dietary-emoji" title="Vegetarian">
          🥬
        </span>
      );
    }

    if (item.is_vegan === true) {
      emojis.push(
        <span key="vegan" className="dietary-emoji" title="Vegan">
          🌱
        </span>
      );
    }

    return emojis.length > 0 ? emojis : null;
  };

  // Handle card click but prevent when clicking on image navigation
  const handleCardClick = e => {
    // If demo locked, redirect to login
    if (isDemoLocked) {
      window.location.href = '/login';
      return;
    }

    // Don't open modal if clicking on image navigation arrows, know more button, or upgrade link
    if (
      e.target.closest('.image-nav-arrow') ||
      e.target.closest('.know-more-button') ||
      e.target.closest('.upgrade-link') ||
      e.target.closest('.calories-clickable')
    ) {
      return;
    }
    onItemClick(item, index);
  };

  // Handle know more button click
  const handleKnowMoreClick = e => {
    e.stopPropagation();
    if (isDemoLocked) {
      window.location.href = '/login';
      return;
    }
    onItemClick(item, index);
  };

  // Handle upgrade link click
  const handleUpgradeClick = e => {
    e.stopPropagation();
    onUpgradeClick();
  };

  // Handle calories click/hover
  const handleCaloriesClick = e => {
    if (!approximateCalories) {
      e.stopPropagation();
      onUpgradeClick();
    }
  };

  const highRiskAllergens = getHighRiskAllergens();

  return (
    <div className={`menu-item-card-airbnb ${isDemoLocked ? 'demo-locked' : ''}`} onClick={handleCardClick}>
      {/* Demo Lock Overlay - moved outside image section to avoid blur inheritance */}
      {isDemoLocked && (
        <div className="demo-lock-overlay">
          <button className="demo-login-button" onClick={() => (window.location.href = '/login')}>
            Sign in for full results & free credits
          </button>
        </div>
      )}

      {/* High-Risk Allergens Section - Always show for paying users to maintain alignment */}
      {(userPlan !== 'free' || highRiskAllergens.length > 0) && (
        <div className="allergen-warning-section">
          {highRiskAllergens.length > 0 ? (
            <>
              <div className="allergen-warning-header">
                <span className="warning-icon">⚠️</span>
                <span className="allergen-warning-text">High-risk allergens:</span>
              </div>
              <div className="allergen-chips">
                {highRiskAllergens.map((allergen, idx) => (
                  <div key={idx} className="allergen-chip">
                    <span className="allergen-icon">{allergen.icon}</span>
                    <span className="allergen-name">{allergen.name}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            /* Empty placeholder for paying users with no high-risk allergens */
            <div className="allergen-warning-placeholder">
              <span className="allergen-warning-text"></span>
            </div>
          )}
        </div>
      )}

      {/* Image Section */}
      <div className="menu-item-image-airbnb">
        {item.loading ? (
          <div className="food-image-placeholder-airbnb loading">
            <span className="spinner"></span>
            <small>Loading images...</small>
          </div>
        ) : imageUrls && imageUrls.length > 0 ? (
          <FoodImage imageUrls={imageUrls} altText={menuName} />
        ) : (
          <div className="food-image-placeholder-airbnb">
            <span className="placeholder-icon-airbnb">🍽️</span>
            <small>No image available</small>
          </div>
        )}

        {/* Know More Button */}
        <button className="know-more-button" onClick={handleKnowMoreClick} aria-label="Know more about this dish">
          {isDemoLocked ? 'Login' : 'Know more'}
        </button>
      </div>

      {/* Content Section */}
      <div className="menu-item-content-airbnb">
        {/* Food Name and Price Row */}
        <div className="food-name-row">
          <h3 className="food-name-airbnb">{menuName}</h3>
          {price && <span className="food-price-airbnb">{price}</span>}
        </div>

        {/* Stats Row - Nutri Score, Dietary Emojis, and Calories */}
        <div className="stats-row-airbnb">
          {/* Nutri Score Display */}
          <div className="nutri-score-section-airbnb">
            <NutriScoreDisplay score={nutritionalScore} size="extra-small" />
          </div>

          {/* Dietary Emojis */}
          <div className="dietary-emojis-row">{renderDietaryEmojis()}</div>

          <div className="calories-airbnb">
            {!approximateCalories ? (
              <div
                className="calories-clickable"
                onClick={handleCaloriesClick}
                onMouseEnter={() => setShowUpgradeTooltip(true)}
                onMouseLeave={() => setShowUpgradeTooltip(false)}
              >
                <span className="calories-text-airbnb">
                  🔥 <span className="blurred">{getRandomCalories()}</span> cal
                </span>
                {showUpgradeTooltip && <div className="upgrade-tooltip">🔒 Upgrade to unlock</div>}
              </div>
            ) : (
              <span className="calories-text-airbnb">🔥 {approximateCalories} cal</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuItemCard;
